import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CategoryGrid from './CategoryGrid';
import CategoryTree from './CategoryTree';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import { useAuth } from '../contexts/AuthContext';
import './AllCategories.css';

const AllCategories = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'tree'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size and set default view to tree
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setViewMode('tree'); // Force tree view on mobile
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };


  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleCategoriesClick = () => {
    // Already on categories page
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleMessagesClick = () => {
    navigate('/messages');
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    console.log('Selected category:', category);
  };

  return (
    <div className="App all-categories-with-bottom-nav">
      <div className="all-categories-nav-header">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={() => navigate('/')}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          currentPage="categories"
        />
      </div>
      
      <div className="all-categories-page">
        <div className="all-categories-header">
          <div className="container">
            <div className="all-categories-header-content">
              <div className="all-categories-title-section">
                <h1>All Categories</h1>
                <p>Explore all available digital service categories</p>
              </div>
              
              <div className="all-categories-controls">
                <div className="view-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => !isMobile && setViewMode('grid')}
                  >
                    <i className="bi bi-grid-3x3-gap"></i>
                    Grid View
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'tree' ? 'active' : ''}`}
                    onClick={() => !isMobile && setViewMode('tree')}
                  >
                    <i className="bi bi-diagram-3"></i>
                    Tree View
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="all-categories-content">
          <div className="container">
            {isMobile || viewMode === 'tree' ? (
              <div className="all-categories-tree-view">
                <CategoryTree
                  onCategorySelect={handleCategorySelect}
                  showIcons={true}
                  showCounts={true}
                  expandAll={false}
                  className="full-width-tree"
                />
              </div>
            ) : (
              <div className="all-categories-grid-view">
                {/* Featured Categories */}
                <CategoryGrid
                  title="Featured Categories"
                  featured={true}
                  size="large"
                  showDescription={true}
                  showSubCategories={true}
                  className="featured-categories"
                  onCategorySelect={handleCategorySelect}
                />

                {/* Main Categories */}
                <CategoryGrid
                  title="Main Categories"
                  level={0}
                  size="medium"
                  showDescription={true}
                  showSubCategories={true}
                  className="main-categories"
                  onCategorySelect={handleCategorySelect}
                />

                {/* Sub Categories */}
                <CategoryGrid
                  title="Sub-Categories"
                  level={1}
                  size="small"
                  showDescription={false}
                  showSubCategories={false}
                  className="sub-categories"
                  onCategorySelect={handleCategorySelect}
                />

                {/* Sub-Sub Categories */}
                <CategoryGrid
                  title="Sub-Sub Categories"
                  level={2}
                  size="small"
                  showDescription={false}
                  showSubCategories={false}
                  className="sub-sub-categories"
                  onCategorySelect={handleCategorySelect}
                />
              </div>
            )}

            {/* Selected Category Info */}
            {selectedCategory && (
              <>
                <div className="selected-category-overlay" onClick={() => setSelectedCategory(null)}></div>
                <div className="selected-category-info">
                  <div className="selected-category-card">
                    <div className="selected-category-header">
                      <h3>Category Details</h3>
                      <button 
                        className="close-btn"
                        onClick={() => setSelectedCategory(null)}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                    <div className="selected-category-content">
                      <div className="selected-category-icon">
                        {selectedCategory.icon ? (
                          <i className={selectedCategory.icon}></i>
                        ) : (
                          <i className="bi bi-tag"></i>
                        )}
                      </div>
                      <div className="selected-category-details">
                        <h4>{selectedCategory.name}</h4>
                        {selectedCategory.description && (
                          <p>{selectedCategory.description}</p>
                        )}
                        <div className="selected-category-meta">
                          <span className="level-badge">
                            Level {selectedCategory.level !== undefined ? selectedCategory.level : 'N/A'}
                          </span>
                          {selectedCategory.is_featured && (
                            <span className="featured-badge">
                              <i className="bi bi-star-fill"></i>
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      <Footer />
      
      {/* Bottom Navigation Bar - Visible only on mobile */}
      <BottomNavigationBar
        onHomeClick={() => navigate('/')}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
};

export default AllCategories;
